import React, { useState, useRef, useEffect } from 'react';
import { Map, GoogleApiWrapper, Marker } from 'google-maps-react';
import location from "../../assets/icons/location.svg";
import { ReactComponent as LocationIcon } from "../../assets/icons/location.svg";

const GoogleMaps = ({ google, onLocationSelect, initialPosition, initialAddress }) => {
  const [position, setPosition] = useState(initialPosition || { 
    lat: 31.507877, 
    lng: 74.336255 
  });
  const [searchInput, setSearchInput] = useState(initialAddress || "");
  const [searchResults, setSearchResults] = useState([]);
  const [placeSelected, setPlaceSelected] = useState(true);
  const mapRef = useRef(null);

  const mapStyles = {
    width: '100%',
    height: '100%',
    position: 'relative',
    borderRadius: '8px',
  };

  const containerStyle = {
    position: 'relative',
    width: '100%',
    height: '400px',
    marginTop: '8px',
  };

  // Update position when initialPosition changes
  useEffect(() => {
    if (initialPosition) {
      setPosition(initialPosition);
      
      // Center the map on the new position
      if (mapRef.current && mapRef.current.map) {
        mapRef.current.map.setCenter(initialPosition);
        mapRef.current.map.setZoom(14);
      }
    }
  }, [initialPosition]);

  // Update searchInput when initialAddress changes
  useEffect(() => {
    if (initialAddress !== undefined) {
      setSearchInput(initialAddress);
      setPlaceSelected(!!initialAddress);
    }
  }, [initialAddress]);

  // Reset search results when search input changes
  useEffect(() => {
    if (!searchInput) {
      setSearchResults([]);
    }
  }, [searchInput]);

  const handleMarkerDragEnd = (_, map, coord) => {
    const { latLng } = coord;
    const lat = latLng.lat();
    const lng = latLng.lng();

    setPosition({ lat, lng });

    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        const address = results[0].formatted_address;
        onLocationSelect({
          lat,
          lng,
          placeId: results[0].place_id,
          placeName: address,
        });
        setSearchInput(address);
      }
    });
  };

  const handleUserSearch = () => {
    if (!searchInput.trim()) return;

    const service = new google.maps.places.AutocompleteService();
    service.getPlacePredictions(
      {
        input: searchInput,
        types: ['geocode', 'establishment'] // This will search for both addresses and places
      },
      (predictions, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSearchResults(predictions);
        } else {
          setSearchResults([]);
        }
      }
    );
  };

  const handlePlaceSelect = (place) => {
    if (!mapRef.current) return;

    const service = new google.maps.places.PlacesService(mapRef.current.map);
    service.getDetails(
      {
        placeId: place.place_id,
        fields: ['geometry', 'formatted_address', 'name']
      },
      (result, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && result) {
          const newPosition = {
            lat: result.geometry.location.lat(),
            lng: result.geometry.location.lng()
          };

          setPosition(newPosition);
          onLocationSelect({
            lat: newPosition.lat,
            lng: newPosition.lng,
            placeId: place.place_id,
            placeName: result.formatted_address || result.name,
          });
          
          setSearchInput(result.formatted_address || result.name);
          setPlaceSelected(true);
          setSearchResults([]);

          // Center and zoom the map
          if (mapRef.current) {
            mapRef.current.map.setCenter(newPosition);
            mapRef.current.map.setZoom(15);
          }
        }
      }
    );
  };

  const handleInputChange = (e) => {
    setSearchInput(e.target.value);
    setPlaceSelected(false);
    if (e.target.value) {
      handleUserSearch();
    } else {
      setSearchResults([]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleUserSearch();
    }
  };

  if (!google) {
    return <div>Loading Maps...</div>;
  }

  // Function to create custom map options to reposition controls
  const createMapOptions = () => {
    return {
      fullscreenControl: false,
      mapTypeControl: false,
      mapTypeControlOptions: {
        position: google.maps.ControlPosition.TOP_RIGHT,
        style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR
      },
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_CENTER
      },
      streetViewControl: true,
      streetViewControlOptions: {
        position: google.maps.ControlPosition.RIGHT_BOTTOM
      }
    };
  };

  return (
    <div style={containerStyle}>
      {/* Search bar with improved positioning */}
      <div className="absolute z-[99] top-[10px] left-0 right-0 px-4 flex justify-center">
        <div className="relative w-full max-w-[500px]">
          <input
            className="navSearchBar h-[40px] w-full ps-[4%] pe-[14%] text-[12px] font-[400] font-poppins outline-none bg-white text-[#303030] rounded-md shadow-md"
            placeholder="Search location..."
            value={searchInput}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
          />
          <LocationIcon 
            className="absolute right-[5%] top-1/2 transform -translate-y-1/2 w-[20px] h-[20px]"
          />
          {searchResults.length > 0 && !placeSelected && (
            <div className="absolute top-full left-0 w-full mt-1 bg-white rounded-md shadow-lg max-h-[200px] overflow-y-auto z-[1001]">
              {searchResults.map((result) => (
                <div
                  key={result.place_id}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-[12px] font-poppins"
                  onClick={() => handlePlaceSelect(result)}
                >
                  {result.description}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Map container with adjusted top padding for controls */}
      <div className="h-full w-full">
        <Map
          google={google}
          zoom={14}
          style={mapStyles}
          initialCenter={position}
          center={position}
          className="map-container"
          ref={mapRef}
          options={createMapOptions()}
        >
          <Marker
            position={position}
            draggable={true}
            onDragend={handleMarkerDragEnd}
            icon={{
              url: location,
              scaledSize: new google.maps.Size(40, 40)
            }}
          />
        </Map>
      </div>
    </div>
  );
};

export default GoogleApiWrapper({
  apiKey: 'AIzaSyAuiAzZQ1Pbs3Ci6DmmgMztdhLjFV5a4tg',
  libraries: ['places'],
  version: '3.exp'
})(GoogleMaps);
